import React, {
  useRef,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { Link, useHistory } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import {
  FaIdCard,
  FaEye,
  FaArrowAltCircleRight,
  FaTrashAlt,
} from 'react-icons/fa';
import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import { formataTelefone, formataCPF } from '../../utils/funcoes';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import Loading from '../../components/Loading';

import getValidationErrors from '../../utils/getValidationErrors';

import getResponseErrors from '../../utils/getResponseErrors';
import SelectAsync from '../../components/SelectAsync';
import AgendamentosPaciente from '../../components/Agendamento/AgendamentosPaciente';
import Modal from '../../components/Modal';

interface IPaciente {
  PacCodigo: number;
  PacPessoaFisica: boolean;
  PacNome: string;
  PacDataNasc: Date;
  PacCPF: string;
  PacCNPJ: string;
  PacTelefone: string;
  PacCelular: string;
  PacEmail: string;
  avatar_url: string;
  idade: string;
  dataNascFormatada: string;
  telefoneFormatado: string;
}

export interface IAgendamentosPaciente {
  AgeCodigo: number;
  AgeInicio: Date;
  AgeFim: Date;
  AgeStatus: number;
  AgeConfirmado: number;
  ConNome: string;
  AgeMedUsuCodigo?: number;
  AgeObs?: string;
  ConAtende?: boolean;
  configuracaoAgenda?: {
    ConAtende: number;
    ConNome: string;
  };
}

interface IRetornoStatusParcelaCartaoDTO {
  PlaCodigo: number;
  Status: string;
  Parcelas: number;
  color: string;
}

export interface IPacienteMaisCartaoDTO extends IPaciente {
  possuiCartao: boolean;
  cartao: IRetornoStatusParcelaCartaoDTO[];
  agendamentosPaciente: IAgendamentosPaciente[];
  atendimentos: number;
  faltas: number;
}

const Paciente: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const { user } = useAuth();

  useEffect(() => {
    if (user.UsuCodigo !== 1) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuCodigo]);

  const formRefPaciente1 = useRef<FormHandles>(null);
  const [paciente1, setPaciente1] = useState<IPacienteMaisCartaoDTO>();
  const [paciente2, setPaciente2] = useState<IPacienteMaisCartaoDTO>();
  const [exibeBotao, setExibeBotao] = useState(true);
  const [showModalExcluir, setShowModalExcluir] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleFormataDadosPaciente = useCallback(
    (dados: IPacienteMaisCartaoDTO) => {
      const pacienteFormatado = {
        ...dados,
        PacCPF: formataCPF(dados.PacCPF),
        PacCelular: formataTelefone(dados.PacCelular),
        PacTelefone: formataTelefone(dados.PacTelefone),
        cartao: dados.cartao.map(cart => {
          return {
            ...cart,
            color: 'danger',
          };
        }),
      };

      return pacienteFormatado;
    },
    [],
  );

  const handleBuscaPaciente1 = useCallback(
    async ({ value }) => {
      try {
        setLoading(true);
        const response = await api.get<IPacienteMaisCartaoDTO>(
          `/pacientes/pacientemaiscartao/${value}`,
        );

        setPaciente1(handleFormataDadosPaciente(response.data));
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefPaciente1.current?.setErrors(errors);
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar os dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [handleFormataDadosPaciente, addToast],
  );

  const handleBuscaPaciente2 = useCallback(
    async ({ value }) => {
      try {
        setLoading(true);
        const response = await api.get<IPacienteMaisCartaoDTO>(
          `/pacientes/pacientemaiscartao/${value}`,
        );

        setPaciente2(handleFormataDadosPaciente(response.data));
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefPaciente1.current?.setErrors(errors);
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar os dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [handleFormataDadosPaciente, addToast],
  );

  const podeTrasnferir = useMemo(() => {
    if (paciente1 && paciente2) return true;
    return false;
  }, [paciente1, paciente2]);

  const handleUnificarPaciente = useCallback(async () => {
    if (paciente1 && paciente2) {
      try {
        setLoading(true);
        const dados = {
          PacCodigoOrigem: paciente1.PacCodigo,
          PacCodigoDestino: paciente2.PacCodigo,
        };
        await api.post(`/pacientes/unificar`, dados);
        setExibeBotao(false);
        addToast({
          title: 'Dados Transferidos.',
          type: 'success',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefPaciente1.current?.setErrors(errors);
        }
        addToast({
          title: 'Não foi possível buscar os dados',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
  }, [addToast, paciente1, paciente2]);

  const handleDeletePaciente = useCallback(async () => {
    setLoading(true);
    setShowModalExcluir(false);
    try {
      await api.delete(`/pacientes/${paciente1?.PacCodigo}`);

      setPaciente1(undefined);
      addToast({
        type: 'success',
        title: 'Registro excluído',
      });
    } catch (error) {
      const mensagemErro = getResponseErrors(error);

      addToast({
        type: 'error',
        title: 'Não foi possível excluir o registro',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, paciente1?.PacCodigo]);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      {showModalExcluir && (
        <Modal
          handleFecharModal={() => setShowModalExcluir(false)}
          show={showModalExcluir}
          titulo="Excluir registro"
          large={false}
        >
          <p className="my-3">
            Essa operação não pode ser desfeita, deseja realmente prosseguir?
          </p>
          <div className="d-flex justify-content-between mt-2">
            <Button
              type="button"
              variant="danger"
              onClick={handleDeletePaciente}
              size="sm"
            >
              <FaTrashAlt /> Sim, excluir
            </Button>

            <Button
              type="submit"
              variant="secondary"
              size="sm"
              onClick={() => setShowModalExcluir(false)}
            >
              Não, voltar
            </Button>
          </div>
        </Modal>
      )}
      <>
        <div className="bg-white border p-2 mb-2">
          <div className="d-flex justify-content-between mb-2">
            <h2 id="topoBusca">Unificação de Pacientes</h2>
          </div>

          <Form
            ref={formRefPaciente1}
            style={{ minHeight: '300px', padding: '.5rem' }}
            onSubmit={handleUnificarPaciente}
          >
            <Row>
              <Col>
                <SelectAsync
                  name="paciente1"
                  label="Paciente Origem"
                  urlApi="pacientes/combo?termoBusca="
                  placeholder="Digite para buscar"
                  handleSelectChange={handleBuscaPaciente1}
                />

                <div className="w-100" />
                <div className="bg-white border p-2 mt-2">
                  {paciente1 ? (
                    <div
                      className="row no-gutter"
                      key={`paciente1-${paciente1.PacCodigo}`}
                      id={`paciente1-${paciente1.PacCodigo}`}
                    >
                      {/* Paciente */}
                      <div className="col-9">
                        <div
                          className="d-flex flex-wrap"
                          style={{ gap: '4px' }}
                        >
                          <div>
                            <b>
                              #{paciente1.PacCodigo} - {paciente1.PacNome}
                            </b>
                            <Link
                              to={`pacientecadastro/${paciente1.PacCodigo}`}
                              target="_blank"
                            >
                              <FaEye size={20} />
                            </Link>
                          </div>

                          {paciente1.PacPessoaFisica ? (
                            <div className="w-100">
                              <span>Nascimento:</span>{' '}
                              {paciente1.dataNascFormatada} - {paciente1.idade}{' '}
                              <span>CPF:</span> {paciente1.PacCPF}
                            </div>
                          ) : (
                            <>
                              <span>CNPJ:</span> {paciente1.PacCNPJ}
                            </>
                          )}
                          <div className="w-100">
                            <span>Celular: </span> {paciente1.PacCelular}{' '}
                            <span>Telefone: </span> {paciente1.PacTelefone}
                          </div>
                        </div>
                      </div>
                      {/* Cartão */}
                      <div className="col-3">
                        {paciente1.cartao && paciente1.cartao.length > 0 && (
                          <div
                            className={`alert alert-${paciente1.cartao[0].color} mb-0 d-block`}
                            style={{ maxWidth: '200px' }}
                          >
                            <Link
                              to={`/cartaosaudedetalhe/${paciente1.cartao[0].PlaCodigo}`}
                              className={`d-flex justify-items-center text-${paciente1.cartao[0].color}`}
                            >
                              <FaIdCard size={13} className="mr-1" /> Cartão
                              Saúde {paciente1.cartao[0].Status}
                            </Link>
                          </div>
                        )}
                      </div>
                      {/* Agendamentos */}
                      <div className="col-12 mt-3">
                        <h6>Agendamentos</h6>
                        <AgendamentosPaciente
                          PacCodigo={paciente1.PacCodigo}
                          PacNome={paciente1.PacNome}
                          agendamentos={paciente1.agendamentosPaciente}
                          atendimentos={paciente1.atendimentos}
                          faltas={paciente1.faltas}
                        />
                      </div>
                      {exibeBotao && (
                        <div className="col-12 mt-3">
                          <Button
                            size="lg"
                            disabled={!podeTrasnferir}
                            type="submit"
                          >
                            Transferir Historico <FaArrowAltCircleRight />
                          </Button>
                          <p>
                            <br />
                            <ul className="list-group">
                              <li className="list-group-item active">
                                Dados a transferir
                              </li>
                              <li className="list-group-item">Agendamentos</li>
                              <li className="list-group-item">Faltas</li>
                              <li className="list-group-item">
                                Cartão/Dependentes
                              </li>
                              <li className="list-group-item">Anexos</li>
                              <li className="list-group-item">
                                Documentos/Receitas
                              </li>
                              <li className="list-group-item">Exames</li>
                            </ul>
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <h5 className="p-5 text-center">
                      Selecione o paciente origem para cópia
                    </h5>
                  )}
                  {paciente1 && (
                    <Button
                      type="button"
                      variant="outline-danger"
                      onClick={() => setShowModalExcluir(true)}
                    >
                      <FaTrashAlt /> Excluir
                    </Button>
                  )}
                </div>
              </Col>
              <Col>
                <SelectAsync
                  name="paciente2"
                  label="Paciente Destino"
                  urlApi="pacientes/combo?termoBusca="
                  placeholder="Digite para buscar"
                  handleSelectChange={handleBuscaPaciente2}
                />
                <div className="w-100" />
                <div className="bg-white border p-2 mt-2">
                  {paciente2 ? (
                    <div
                      className="row no-gutter"
                      key={`paciente2-${paciente2.PacCodigo}`}
                      id={`paciente2-${paciente2.PacCodigo}`}
                    >
                      {/* Paciente */}
                      <div className="col-9">
                        <div
                          className="d-flex flex-wrap"
                          style={{ gap: '4px' }}
                        >
                          <div>
                            <b>
                              #{paciente2.PacCodigo} - {paciente2.PacNome}
                            </b>
                            <Link
                              to={`pacientecadastro/${paciente2.PacCodigo}`}
                              target="_blank"
                            >
                              <FaEye size={20} />
                            </Link>
                          </div>

                          {paciente2.PacPessoaFisica ? (
                            <div className="w-100">
                              <span>Nascimento:</span>{' '}
                              {paciente2.dataNascFormatada} - {paciente2.idade}{' '}
                              <span>CPF:</span> {paciente2.PacCPF}
                            </div>
                          ) : (
                            <>
                              <span>CNPJ:</span> {paciente2.PacCNPJ}
                            </>
                          )}
                          <div className="w-100">
                            <span>Celular: </span> {paciente2.PacCelular}{' '}
                            <span>Telefone: </span> {paciente2.PacTelefone}
                          </div>
                        </div>
                      </div>
                      {/* Cartão */}
                      <div className="col-3">
                        {paciente2.cartao && paciente2.cartao.length > 0 && (
                          <div
                            className={`alert alert-${paciente2.cartao[0].color} mb-0 d-block`}
                            style={{ maxWidth: '200px' }}
                          >
                            <Link
                              to={`/cartaosaudedetalhe/${paciente2.cartao[0].PlaCodigo}`}
                              className={`d-flex justify-items-center text-${paciente2.cartao[0].color}`}
                            >
                              <FaIdCard size={13} className="mr-1" /> Cartão
                              Saúde {paciente2.cartao[0].Status}
                            </Link>
                          </div>
                        )}
                      </div>
                      {/* Agendamentos */}
                      <div className="col-12 mt-3">
                        <h6>Agendamentos</h6>
                        <AgendamentosPaciente
                          PacCodigo={paciente2.PacCodigo}
                          PacNome={paciente2.PacNome}
                          agendamentos={paciente2.agendamentosPaciente}
                          atendimentos={paciente2.atendimentos}
                          faltas={paciente2.faltas}
                        />
                      </div>
                    </div>
                  ) : (
                    <h5 className="p-5 text-center">
                      Selecione o paciente destino
                    </h5>
                  )}
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </>
    </Container>
  );
};

export default Paciente;
